import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoWordmark } from "./Logo";

const NAV = [
  { label: "What You Get", href: "/#what-you-get" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Our Work", href: "/#our-work" },
  { label: "Pricing", href: "/pricing" },
  { label: "FAQs", href: "/#faqs" },
];

export const SiteHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // Empty deps: scroll listener attaches once for the lifetime of the header.
    // setScrolled is a stable setState function; onScroll is defined inside the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the mobile menu whenever the user navigates. setOpen is a stable setState function.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`site-header fixed top-0 left-0 right-0 z-50 border-b border-transparent ${scrolled ? "scrolled" : ""}`}
      data-testid="site-header"
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div
          className="flex items-center justify-between transition-all"
          style={{ height: scrolled ? 64 : 84 }}
        >
          <Link to="/" data-testid="logo-link" aria-label="ReadyTrades home">
            <LogoWordmark size={scrolled ? 30 : 34} />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-4 py-2 text-[15px] font-medium text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors rounded-full"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <Link to="/admin" className="btn-ghost" data-testid="sign-in-link">Sign In</Link>
            <Link to="/get-started" className="btn-primary" data-testid="header-cta">
              Get Your Free Preview
            </Link>
          </div>

          <button
            className="lg:hidden p-2 -mr-2 text-[color:var(--ink)]"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-btn"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[color:var(--bg)] border-t border-[color:var(--border-soft)]" data-testid="mobile-menu">
          <div className="max-w-[1240px] mx-auto px-6 py-5 space-y-1">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="block px-3 py-3 text-[color:var(--ink)] font-medium border-b border-[color:var(--border-soft)] last:border-0"
              >
                {item.label}
              </a>
            ))}
            <div className="pt-4 flex flex-col gap-2">
              <Link to="/admin" className="btn-secondary w-full justify-center">Sign In</Link>
              <Link to="/get-started" className="btn-primary w-full justify-center">Get Your Free Preview</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;
